import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Card, SectionLabel, StatusBadge, TICKET_STATUS_TONE, TICKET_STATUS_LABEL } from "../../components/customer-app/ui";
import { ChevronLeft, CheckCircle2, Circle, Loader2, PartyPopper, Sparkles, Hash, Calendar, Ticket, Users, ShieldAlert, ShieldCheck } from "lucide-react";

function ActivityIcon({ status }) {
  if (status === "completed") return <CheckCircle2 className="w-4 h-4 text-emerald-600" strokeWidth={2.5} />;
  if (status === "in_process") return <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />;
  return <Circle className="w-4 h-4 text-slate-300" strokeWidth={2} />;
}

/** Restricted read-only progress view for a friend the current customer referred.
 * Never surfaces quotation pricing — only serviceability, activity/phase progress,
 * and installation status. Once the friend logs in themselves, they see their own full view. */
export default function CustomerReferralProgress() {
  const { leadId } = useParams();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/customer-app/referrals/${leadId}/progress`);
        setData(data);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally { setLoading(false); }
    })();
  }, [leadId]);

  if (loading) {
    return <div className="pt-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0D4721]" /></div>;
  }
  if (!data) return null;

  const installDone = ["installation_completed", "closed"].includes(data.installation?.installation_status);

  return (
    <div className="pb-6" data-testid="customer-referral-progress">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 mb-3">
        <ChevronLeft className="w-3.5 h-3.5" /> Back
      </button>

      <div className="rounded-2xl p-5 text-white bg-gradient-to-br from-amber-600 via-amber-500 to-[#0D4721]">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-white/80 font-bold bg-white/15 px-2 py-1 rounded-full">
          <Users className="w-3 h-3" /> Your Reference
        </div>
        <div className="text-lg font-extrabold mt-2">{data.customer_name}</div>
        <div className="text-[11px] font-mono text-white/70 mt-0.5">{data.code}</div>
        {data.is_serviceable === false ? (
          <div className="mt-3 flex items-center gap-1.5 text-xs font-bold bg-red-500/20 text-red-50 px-2.5 py-1.5 rounded-lg w-fit" data-testid="referral-not-serviceable-badge">
            <ShieldAlert className="w-3.5 h-3.5" /> Non Serviceable Area
          </div>
        ) : (
          <div className="mt-3 flex items-center gap-1.5 text-xs font-bold bg-emerald-500/20 text-emerald-50 px-2.5 py-1.5 rounded-lg w-fit" data-testid="referral-serviceable-badge">
            <ShieldCheck className="w-3.5 h-3.5" /> Serviceable
          </div>
        )}
        {data.stage === "opportunity" && data.is_serviceable !== false && (
          <div className="mt-4 flex items-end justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/60">Overall Progress</div>
              <div className="text-4xl font-black mt-0.5" data-testid="referral-overall-pct">{data.overall_pct}<span className="text-lg text-white/60">%</span></div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-white/60">Phase</div>
              <div className="text-lg font-bold text-white">{data.current_phase} / 5</div>
            </div>
          </div>
        )}
        {data.stage === "opportunity" && data.is_serviceable !== false && (
          <div className="mt-3 h-2 rounded-full bg-white/20 overflow-hidden">
            <div className="h-full rounded-full bg-white" style={{ width: `${data.overall_pct}%` }} />
          </div>
        )}
      </div>

      <div className="mt-3 text-[11px] text-slate-400 text-center">
        Pricing & quotation details are only visible to your friend once they sign in on the app themselves.
      </div>

      {data.stage === "lead" && (
        <Card className="mt-4 p-4 text-center" data-testid="referral-awaiting-consultant">
          <div className="text-sm font-semibold text-slate-700">Awaiting our solar consultant</div>
          <div className="text-xs text-slate-500 mt-1">We'll reach out to them shortly to plan the visit.</div>
        </Card>
      )}

      {installDone && (
        <Card className="mt-4 p-4 bg-gradient-to-br from-emerald-50 to-amber-50 border-emerald-300" data-testid="referral-install-complete-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
              <PartyPopper className="w-5 h-5 text-white" />
            </div>
            <div className="text-sm font-extrabold text-emerald-800">Installation Completed!</div>
          </div>
          <div className="mt-3.5 pt-3.5 border-t border-emerald-200/70 space-y-2.5">
            {data.installation.ticket_number && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs text-emerald-700"><Ticket className="w-3.5 h-3.5" /> Ticket Number</div>
                <span className="text-xs font-mono font-bold text-emerald-900">{data.installation.ticket_number}</span>
              </div>
            )}
            {data.installation.commissioning_date && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs text-emerald-700"><Calendar className="w-3.5 h-3.5" /> Date of Installation</div>
                <span className="text-xs font-bold text-emerald-900">{new Date(data.installation.commissioning_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
              </div>
            )}
          </div>
        </Card>
      )}
      {!installDone && data.installation && data.installation.installation_status !== "not_logged" && (
        <Card className="mt-4 p-3.5" data-testid="referral-install-status-banner">
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <div className="text-sm font-semibold text-slate-700">{data.installation.installation_status_label}</div>
          </div>
          {data.installation.ticket_status && (
            <div className="flex items-center justify-between mt-2.5 pt-2.5 border-t border-slate-100">
              <div className="text-xs text-slate-500">Ticket Status</div>
              <StatusBadge tone={TICKET_STATUS_TONE[data.installation.ticket_status]}>{TICKET_STATUS_LABEL[data.installation.ticket_status]}</StatusBadge>
            </div>
          )}
        </Card>
      )}

      {data.stage === "opportunity" && data.phases?.length > 0 && (
        <div className="mt-6">
          <SectionLabel className="mb-3">Five Phases — Project Journey</SectionLabel>
          <div className="space-y-5">
            {data.phases.map((p, i) => {
              const isCurrent = p.phase === data.current_phase;
              const allDone = p.total > 0 && p.done === p.total;
              return (
                <div key={p.phase} className={"relative pl-7 " + (i < data.phases.length - 1 ? "pb-5 border-l-2 ml-2.5" : "ml-2.5") + " " + (allDone ? "border-emerald-400" : "border-slate-200")}>
                  <div className={"absolute -left-[11px] top-0 w-5.5 h-5.5 rounded-full flex items-center justify-center text-[10px] font-black " + (allDone ? "bg-emerald-600 text-white" : isCurrent ? "bg-amber-400 text-[#0D4721] ring-4 ring-amber-200" : "bg-white border-2 border-slate-200 text-slate-400")}>
                    {allDone ? <CheckCircle2 className="w-3 h-3" strokeWidth={3} /> : p.phase}
                  </div>
                  <div className="text-sm font-bold text-slate-800">{p.phase_name}</div>
                  <div className="text-[11px] font-mono text-slate-400 mt-0.5">{p.done}/{p.total} done{isCurrent && !allDone ? " · current" : ""}</div>
                  <ul className="mt-2 space-y-1.5">
                    {p.activities.map((a, ai) => (
                      <li key={ai} className="flex items-start gap-2 text-xs text-slate-600">
                        <ActivityIcon status={a.status} /> <span className="mt-0.5">{a.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
